import type { BatchWorkspaceOverview, LiveCaptureProgress } from '../shared/desktop-api';
import { askedWork } from './document-work-filter';

/** Initial landing only (the first document asked about, else the first): live review updates must not steal the operator's place. */
export const openingDocumentIndex = (
  documents: readonly { needsReview: boolean; needsTeaching?: boolean }[],
  calibrating: boolean,
): number =>
  Math.max(
    0,
    documents.findIndex((item) => askedWork(item, calibrating) !== null),
  );

/**
 * Which room a job opens into, once its saved batch has been asked for.
 *
 * There are three answers and the workspace only ever had two. It asked the
 * engine for the job's latest batch and, on a null, showed the intake screen —
 * "Feed it pages." — because a job with no batch has always meant a job nobody
 * has scanned into yet.
 *
 * It does not. It also means a job whose FIRST acquisition is still in flight:
 * neither empty nor readable, because there is nothing committed to read and
 * pages are going through the reader as the question is asked. An operator two
 * documents into a hundred stepped out to the Helper computers screen, came
 * back, and was asked to feed pages to a workstation that had been reading his
 * for five minutes. Nothing on that screen could reach the documents, because
 * the documents were not saved yet — so the intake screen was not merely the
 * wrong room, it was a room with no door out until the batch happened to land.
 */
export type OpeningJobScreen =
  /** The batch exists. Load it and land on the workspace. */
  | { screen: 'workspace'; overview: BatchWorkspaceOverview }
  /** No batch yet, but this job is reading. Keep waiting, and say what it is doing. */
  | { screen: 'still-reading'; progress: LiveCaptureProgress }
  /** No batch and nothing in flight. The job really is empty. */
  | { screen: 'intake' };

export const decideOpeningScreen = (
  overview: BatchWorkspaceOverview | null | undefined,
  liveProgress: LiveCaptureProgress | null | undefined,
): OpeningJobScreen => {
  if (overview) return { screen: 'workspace', overview };
  if (liveProgress) return { screen: 'still-reading', progress: liveProgress };
  return { screen: 'intake' };
};

/**
 * The number an operator is watching may not fall.
 *
 * It read 2, then 0, then 1 — because each of those came from a different
 * place: the workspace's own spine, a progress counter rebuilt from nothing,
 * and a batch read back after the fact. The fix is one source, which is the
 * retained live progress, and everything below is what one source still cannot
 * promise on its own.
 *
 * A stage boundary legitimately resets the count: "page 812 of 900" becomes
 * "document 1 of 40" because the second sentence counts different things, and
 * it is labelled with what it counts. A different wave counts its own pages. So
 * what is held here is narrow and exact — inside one wave and one stage, the
 * count only ever goes up — and it exists because the alternative is an
 * operator watching a total tick backwards and drawing the only available
 * conclusion, which is that the software lost their work.
 */
export const advanceOpeningProgress = (
  shown: LiveCaptureProgress | null,
  next: LiveCaptureProgress,
): LiveCaptureProgress => {
  if (!shown || shown.captureId !== next.captureId || shown.stage !== next.stage) return next;
  return next.completed >= shown.completed
    ? next
    : {
        ...shown,
        // A recovered retry can report an older count while its durable wall
        // clock is newer. Hold the work count, not the old timestamp — otherwise
        // the "elapsed" label freezes at the exact moment recovery begins.
        elapsedMilliseconds: Math.max(shown.elapsedMilliseconds, next.elapsedMilliseconds),
        stageElapsedMilliseconds: Math.max(
          shown.stageElapsedMilliseconds,
          next.stageElapsedMilliseconds,
        ),
      };
};
